/* eslint-disable no-shadow */
import { sessionStorage } from '@/app/shared/services/sessionStorage.service';
import { userApi } from '@/app/shared/services/apis/userApi.service';
import { UserPermissionsService } from '@/app/shared/services/userPermissions.service';
import { updateAtIndex } from '@fifa-bsm/bo-frontend-shared/utils';

const state = () => ({
    user: null,
    adminAuthorization: {
        rememberedUser: null,
    },
    loadingUser: false,
    mainEventRoles: [],
    eventRoles: [],
});

const getters = {
    user(state) {
        return state.user;
    },
    userId(state) {
        return state.user?.id;
    },
    isAdmin(state) {
        return state.user?.is_admin;
    },
    userGroupCode(state) {
        return state.user?.group?.code;
    },
    loadingUser(state) {
        return state.loadingUser;
    },
    userPermissions(state, getters) {
        const permissionsService = new UserPermissionsService(
            getters.user,
            getters.mainEventRolesMap,
            getters.eventRolesMap
        );
        return permissionsService.permissions;
    },
    systemPermissionsMap(state) {
        return state.user.permissions.reduce((permissions, permission) => {
            permissions[permission.name] = permission.has_permission;
            return permissions;
        }, {});
    },
    mainEventRolesMap(state) {
        return state.mainEventRoles.reduce((map, role) => {
            map[role.mainEventId] = role.roles.reduce((rolesMap, role) => {
                rolesMap[role.code] = true;
                return rolesMap;
            }, {});
            return map;
        }, {});
    },
    eventRolesMap(state) {
        return state.eventRoles.reduce((map, role) => {
            map[role.eventId] = role.roles.reduce((rolesMap, role) => {
                rolesMap[role.code] = true;
                return rolesMap;
            }, {});
            return map;
        }, {});
    },
};

const actions = {
    async fetchUserData({ commit, dispatch }) {
        commit('setLoadingUser', true);
        try {
            const userResponse = await userApi.getUserData();
            commit('setUserData', userResponse.user);
            await dispatch('loadEventsPermissions');
        } catch (error) {
            if (error.response?.status === 401) {
                dispatch('Auth/signOut', undefined, { root: true });
            }
        } finally {
            commit('setLoadingUser', false);
        }
    },

    async refreshUserData({ commit }) {
        const userResponse = await userApi.getUserData();
        commit('setUserData', userResponse.user);
    },

    async loadEventsPermissions({ commit }) {
        const response = await userApi.getEventsRoles();
        const mainEventRoles = response['main_events'];
        const eventRoles = response['events'];

        mainEventRoles.forEach((permissionsForMainEvent) => {
            commit('updateRoleForMainEvent', {
                mainEventId: permissionsForMainEvent.main_event_id,
                roles: permissionsForMainEvent.roles,
            });
        });

        eventRoles.forEach((permissionsForEventByMainEvent) => {
            permissionsForEventByMainEvent.events.forEach((permissionsForEvent) => {
                commit('updateRolesForEvent', {
                    eventId: permissionsForEvent.event_id,
                    mainEventId: permissionsForEventByMainEvent.main_event_id,
                    roles: permissionsForEvent.roles,
                });
            });
        });
    },
};

const mutations = {
    setUserData(state, userData) {
        sessionStorage.set('user', userData);
        state.user = userData;
    },
    setLoadingUser(state, loading) {
        state.loadingUser = loading;
    },
    updateRoleForMainEvent(state, { mainEventId, roles }) {
        const existingPermissionIndex = state.mainEventRoles.findIndex(
            (permission) => permission.mainEventId === mainEventId
        );

        const permissions = {
            mainEventId,
            roles: roles.map((role) => ({ id: role.role_id, code: role.role_code })),
        };

        if (existingPermissionIndex !== -1) {
            state.mainEventRoles = updateAtIndex(
                permissions,
                existingPermissionIndex,
                state.mainEventRoles
            );
            return;
        }

        state.mainEventRoles = [...state.mainEventRoles, permissions];
    },
    updateRolesForEvent(state, { eventId, mainEventId, roles }) {
        state.eventRoles = [
            ...state.eventRoles,
            {
                mainEventId,
                eventId,
                roles: roles.map((role) => ({
                    id: role.role_id,
                    code: role.role_code,
                })),
            },
        ];
    },
    setAdminAuthorizationRememberedUser(state, user) {
        state.adminAuthorization.rememberedUser = user;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
